import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  message,
} from "antd";
import { ClearOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import OptionService from "../../../../service/ERP/Options.service";
import { accessColumns } from "./model";

const optionService = OptionService();
const WAITING_PRODUCTION_STATUS = "รอผลิต";

export default function ModalProductOrderSelector({
  open,
  close,
  onSelect,
  selectedRecords = [],
  selectedProduceType = "FG",
  lockedFgCode = null,
}) {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [produceType] = useState("FG");
  const [accessData, setAccessData] = useState([]);
  const [activeSearch, setActiveSearch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionFgCode, setSessionFgCode] = useState(lockedFgCode);
  const { RangePicker } = DatePicker;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (Array.isArray(selectedRecords) && selectedRecords.length > 0) {
      setSelectedRowKeys(
        selectedRecords
          .map((record) => record?.detail_code)
          .filter(Boolean),
      );
      setSelectedRows(selectedRecords.filter(Boolean));
      return;
    }

    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [open, selectedProduceType, selectedRecords]);

  const handleSearch = (forcedValues = null) => {
    const runSearch = async (values) => {
      const data = {
        ...values,
        p: "create_pd",
      };

      if (sessionFgCode) {
        data.fg_code = sessionFgCode;
      }

      if (data?.jodate) {
        const [jodate_form, jodate_to] = data.jodate.map((item) => dayjs(item).format("YYYY-MM-DD"));
        Object.assign(data, { jodate_form, jodate_to });
      }

      setLoading(true);
      try {
        const res = await optionService.optionsJO(data, {
          ignoreLoading: true,
        });
        setAccessData(res?.data?.data || []);
      } catch (err) {
        console.log(err);
        message.error("โหลดรายการ Job Order รอการผลิตไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    };

    if (forcedValues) {
      runSearch(forcedValues);
      return;
    }

    form.validateFields().then((values) => {
      runSearch(values);
    });
  };

  const handleClear = () => {
    form.resetFields();
    if (lockedFgCode) {
      form.setFieldValue("fg_code", lockedFgCode);
    }
    handleSearch({});
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setSessionFgCode(lockedFgCode);
    if (lockedFgCode) {
      form.setFieldValue("fg_code", lockedFgCode);
    }
    handleSearch({});
  }, [open]);

  const displayData = useMemo(
    () =>
      sessionFgCode
        ? accessData.filter((r) => r?.fg_code === sessionFgCode)
        : accessData,
    [accessData, sessionFgCode],
  );

  const columns = useMemo(
    () =>
      accessColumns({
        showAction: false,
      }),
    [],
  );

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,
    onChange: (nextSelectedRowKeys, nextSelectedRows) => {
      setSelectedRowKeys(nextSelectedRowKeys);
      setSelectedRows(nextSelectedRows);

      if (nextSelectedRows.length > 0) {
        const fg = nextSelectedRows[0]?.fg_code || null;
        if (fg !== sessionFgCode) {
          setSessionFgCode(fg);
          form.setFieldValue("fg_code", fg);
        }
      } else if (!lockedFgCode) {
        setSessionFgCode(null);
        form.setFieldValue("fg_code", null);
      }
    },
  };

  const handleConfirm = () => {
    if (!selectedRows?.length) {
      message.warning("กรุณาเลือก Job Order ที่ต้องการสร้าง Production Order");
      return;
    }

    if (typeof onSelect === "function") {
      onSelect(
        selectedRows.map((row) => ({
          ...row,
          produce_type: produceType,
        })),
      );
    }
  };

  // const SectionTop = (
  //   <Flex justify="flex-end" className="mb-1">
  //       <Button
  //         onClick={close}
  //         style={{ minWidth: 96 }}
  //       >
  //         ยกเลิก
  //       </Button>
  //       <Button
  //         className="bn-center justify-center"
  //         type="primary"
  //         style={{ minWidth: 140 }}
  //         onClick={handleConfirm}
  //       >
  //         เลือกรายการ
  //       </Button>
  //   </Flex>
  // );

  return (
    <Modal
      open={open}
      onCancel={close}
      title="เลือก Job Order สำหรับสร้าง Production Order"
      style={{ top: 15, paddingBottom: 10 }}
      footer={[
        <Button key="cancel" onClick={close}>
          ยกเลิก
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          เลือกรายการ
        </Button>,
      ]}
      width={1450}
      destroyOnHidden
    >
      {/* {SectionTop} */}
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {sessionFgCode && (
          <Alert
            type="info"
            showIcon
            message={`FG ที่จะผลิต : ${sessionFgCode} `}
          />
        )}
        {/* <Radio.Group
          value={produceType}
          onChange={(event) => setProduceType(event.target.value)}
          options={[
            { label: "ผลิต FG", value: "FG" },
            { label: "ผลิต WI", value: "WI" },
          ]}
          optionType="button"
          buttonStyle="solid"
        /> */}

        <Collapse
          size="small"
          activeKey={activeSearch}
          bordered={false}
          onChange={(keys) => setActiveSearch(keys)}
          items={[
            {
              key: "1",
              label: (
                <>
                  <SearchOutlined />
                  <span> ค้นหา</span>
                </>
              ),
              showArrow: false,
              children: (
                <>
                  <br />
                  <Form form={form} layout="vertical" autoComplete="off">
                    <Row gutter={[8, 8]}>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="JO Code" name="jocode">
                          <Input placeholder="Enter JO Code" onChange={() => handleSearch()} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="Job Order Date" name="jodate">
                          <RangePicker
                            placeholder={["From Date", "To date"]}
                            style={{ width: "100%", height: 40 }}
                            onChange={() => handleSearch()}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="Customer Name" name="cusname">
                          <Input placeholder="Enter Customer Name" onChange={() => handleSearch()} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="PG Code" name="pg_code">
                          <Input placeholder="Enter PG Code" onChange={() => handleSearch()} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="PG Name" name="pg_name">
                          <Input placeholder="Enter PG Name" onChange={() => handleSearch()} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="FG Code" name="fg_code">
                          <Input
                            placeholder="Enter FG Code"
                            onChange={() => handleSearch()}
                            disabled={!!sessionFgCode}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="FG Name" name="fg_name">
                          <Input placeholder="Enter FG Name" onChange={() => handleSearch()} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                        <Form.Item label="Brand" name="brand">
                          <Input placeholder="Enter Brand" onChange={() => handleSearch()} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[8, 8]}>
                      <Col xs={24}>
                        <Flex justify="flex-end" gap={8}>
                          <Button type="primary" size="small" className="bn-action" icon={<SearchOutlined />} onClick={() => handleSearch()}>
                            ค้นหา
                          </Button>
                          <Button type="primary" size="small" className="bn-action" danger icon={<ClearOutlined />} onClick={handleClear}>
                            ล้าง
                          </Button>
                        </Flex>
                      </Col>
                    </Row>
                  </Form>
                </>
              ),
            },
          ]}
        />

        <Card>
          <Table
            size="small"
            rowKey="detail_code"
            columns={columns}
            dataSource={displayData}
            rowSelection={rowSelection}
            loading={loading}
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        </Card>
      </Space>
    </Modal>
  );
}
